import { Link } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { EmptyState } from '../components/EmptyState';
import { compareBy, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import {
  AGENT_COMMANDS,
  CMD_STATUS_CLASS,
  OP_LABEL,
  timeAgo,
  type AgentCommand,
  type ClusterAgentSettings,
} from '../lib/agentCommands';

/**
 * Command log — every draft/approval/run across the fleet. Mirrors the
 * scheduler list (poll + cache-and-network + client pager) because operators
 * watch this page while a rollout moves.
 */

interface Data {
  agentCommands: AgentCommand[];
  clusterAgentSettings: ClusterAgentSettings[];
}

const STATUSES = [
  'draft',
  'queued',
  'claimed',
  'running',
  'applying',
  'verifying',
  'succeeded',
  'failed',
  'expired',
  'cancelled',
  'rejected',
];

/** The server caps the window; say so rather than letting a truncated list read
 *  as the whole history. */
const LIMIT = 200;

/** Sortable columns → the scalar behind the cell. */
const SORT_PICK: Record<string, (c: AgentCommand) => unknown> = {
  op: (c) => OP_LABEL[c.op] ?? c.op,
  clusterName: (c) => c.clusterName,
  target: (c) => c.agentId ?? c.target,
  status: (c) => c.status,
  requestedBy: (c) => c.requestedBy,
  approvedBy: (c) => c.approvedBy,
  createdAt: (c) => c.createdAt,
};

export function AgentCommandsPage() {
  // Cluster and status are SERVER filters (they go into the query); sort and page
  // are client-side over the returned window. All four in the URL, so "everything
  // that failed on prod-api" is a link you can paste into an incident channel.
  const [cluster, setCluster] = useUrlState('cluster');
  const [status, setStatus] = useUrlState('status');
  const sort = useUrlSort('createdAt', 'desc');
  const [page, setPage] = useUrlNumber('page', 1);

  const { data, loading, error, refetch } = useQuery<Data>(AGENT_COMMANDS, {
    variables: {
      clusterName: cluster || null,
      status: status || null,
      limit: LIMIT,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });

  const commands = data?.agentCommands ?? [];
  const clusters = (data?.clusterAgentSettings ?? []).map((s) => s.clusterName).sort();
  const sorted = compareBy(commands, (c) => SORT_PICK[sort.key]?.(c), sort.dir);
  const pg = usePaged(sorted);
  const capped = commands.length >= LIMIT;

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/agents" className="muted">
          ← Agents
        </Link>
      </div>
      <div className="detail-head">
        <h2>Agent commands</h2>
        <span className="badge quiet">
          {commands.length} command{commands.length === 1 ? '' : 's'}
        </span>
        <button type="button" className="btn btn-ghost" onClick={() => refetch()}>
          Refresh
        </button>
      </div>
      <p className="detail-desc">
        Every command is <strong>previewed, drafted and approved</strong> before an agent can claim it —
        approval echoes the draft&rsquo;s plan digest, so nothing runs that a human did not read. Issue one from
        the <Link to="/agents">fleet page</Link>.
      </p>

      <div className="filter-row">
        <select className="class-select" value={cluster} onChange={(e) => setCluster(e.target.value)}>
          <option value="">all clusters</option>
          {clusters.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select className="class-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">any status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <>
          <p className="error">Couldn&apos;t load the command log — the API didn&apos;t respond.</p>
          <p className="muted">
            Any command already approved keeps running; agents claim from the server, not from this
            page. If it persists, ask in #devops-infra.
          </p>
        </>
      )}

      {loading && !commands.length ? (
        <div className="skeleton-row-group">
          {[0, 1, 2, 3].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      ) : !commands.length ? (
        <EmptyState
          icon="run"
          title={cluster || status ? 'No commands match these filters' : 'No commands yet'}
          body="Issue one from the fleet page — preview, draft, then approve. Nothing runs before a human reads the plan."
          actionLabel="Go to the fleet"
          actionTo="/agents"
        />
      ) : (
        <>
          <table className="data-list--primary">
            <thead>
              <tr>
                <SortHeader label="Command" sortKey="op" sort={sort} />
                <SortHeader label="Cluster" sortKey="clusterName" sort={sort} />
                <SortHeader label="Target" sortKey="target" sort={sort} />
                <SortHeader label="Status" sortKey="status" sort={sort} />
                <SortHeader label="Requested by" sortKey="requestedBy" sort={sort} />
                <SortHeader label="Approved by" sortKey="approvedBy" sort={sort} />
                <SortHeader label="When" sortKey="createdAt" sort={sort} />
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link className="table-title" to={`/agents/commands/${c.id}`}>
                      {OP_LABEL[c.op] ?? c.op}
                    </Link>
                    {/* op / cluster / agent id are resource identifiers, not code
                        snippets — Plex Sans per the phase 6 §1 mono policy. */}
                    <div className="text-2xs muted">
                      #{c.id} · {c.op}
                      {c.mutating ? ' · mutating' : ''}
                    </div>
                  </td>
                  <td className="muted">{c.clusterName}</td>
                  <td className="muted">{c.agentId ?? c.target}</td>
                  <td>
                    <span className={`badge ${CMD_STATUS_CLASS[c.status] ?? ''}`}>{c.status}</span>
                    {c.cancelRequested && <span className="badge inferred">cancelling</span>}
                  </td>
                  <td className="muted">{c.requestedBy}</td>
                  <td className="muted">{c.approvedBy ?? '—'}</td>
                  <td className="muted">{timeAgo(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            start={pg.start}
            pageSize={pg.pageSize}
            onPage={pg.setPage}
          />
          {/* No silent truncation: at the cap this is a WINDOW, and sorting only
              reorders what came back. Narrowing by cluster or status is the way
              to see further back. */}
          {capped && (
            <p className="muted text-xs">
              Showing the {LIMIT} most recent commands{cluster || status ? ' that match' : ''} —
              older ones exist. Narrow by cluster or status to reach them.
            </p>
          )}
        </>
      )}
    </div>
  );
}
